import {
  formatDataManagerAuditFieldLabel,
  formatDataManagerAuditSafeFieldValue,
} from "./dataManagerAuditLabels.js";
import { buildReassignmentDiagnosticPanel } from "./resultsChromeData.js";
import { buildTruckSequenceComparisonCopyText } from "./truckSequenceComparison.js";
import { buildPlanningOrderEditCopyText, buildPlanningOrderEditPresentation } from "./planningOrderEditDiagnostics.js";
import { buildConsolidationCaptureDetails } from "./consolidationCaptures.js";
import { buildCompartmentSharingConfirmationContent } from "./compartmentSharingChallenge.js";

const SOURCE_CHANGES_SNAPSHOT_UNAVAILABLE_REASON_CODE = "source_changes_snapshot_unavailable";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizePlanningLastDiagnosticsPeriod(value) {
  return normalizeInlineText(value).toLowerCase() === "evening" ? "evening" : "day";
}

export function formatPlanningDiagnosticDispatchDate(value) {
  const rawValue = normalizeInlineText(value).slice(0, 10);
  if (!rawValue) {
    return "";
  }
  const parsedDate = new Date(`${rawValue}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat("fr-CA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsedDate);
}

export function formatPlanningDiagnosticScopeLabel({
  dispatchDate = "",
  dispatchPeriod = "day",
  dispatchPeriods,
} = {}) {
  const dateLabel = formatPlanningDiagnosticDispatchDate(dispatchDate);
  if (Array.isArray(dispatchPeriods)) {
    const periods = dispatchPeriods.map((period) => period === "evening" ? "Soir" : "Jour").join(" / ");
    return [dateLabel, periods].filter(Boolean).join(" · ");
  }
  const periodLabel = normalizePlanningLastDiagnosticsPeriod(dispatchPeriod) === "evening"
    ? "Soir"
    : "Jour";
  return dateLabel ? `${dateLabel} · ${periodLabel}` : periodLabel;
}

export function formatPlanningDiagnosticStatusLabel(status = "") {
  const normalized = normalizeInlineText(status).toLowerCase();
  if (normalized === "success") {
    return "Réussi";
  }
  if (normalized === "awaiting_confirmation") return "À confirmer";
  if (normalized === "no_change") {
    return "Aucun changement";
  }
  if (normalized === "refused") {
    return "Refusé";
  }
  if (normalized === "partial") {
    return "Partiel";
  }
  if (normalized === "failed") {
    return "Échec";
  }
  if (normalized === "cancelled") {
    return "Annulé";
  }
  return "Mesuré";
}

export function formatPlanningDiagnosticStatusTone(status = "") {
  const normalized = normalizeInlineText(status).toLowerCase();
  if (normalized === "failed") {
    return "danger";
  }
  if (["cancelled", "refused", "partial", "awaiting_confirmation"].includes(normalized)) {
    return "warning";
  }
  if (normalized === "success") {
    return "success";
  }
  return "info";
}

export function isPlanningDiagnosticProblemStatus(status = "") {
  return ["failed", "refused", "partial", "cancelled"].includes(
    normalizeInlineText(status).toLowerCase(),
  );
}

export function resolvePlanningDiagnosticMetricEmphasis(status = "") {
  if (["partial", "refused"].includes(normalizeInlineText(status).toLowerCase())) return "warning";
  if (isPlanningDiagnosticProblemStatus(status)) {
    return "problem";
  }
  const normalized = normalizeInlineText(status).toLowerCase();
  if (normalized === "success" || normalized === "") {
    return "success";
  }
  return "neutral";
}

const REASSIGNMENT_ACTION_LABELS = {
  consolidation_sheet_save: "Consolidation - Sauvegarder le relevé",
  consolidation_sheet_validate: "Consolidation - Valider le relevé",
  consolidation_quantity_decision: "Consolidation - Conflit de quantités",
  consolidation_preload_save: "Consolidation - Sauvegarder les préchargements",
  consolidation_preload_validate: "Consolidation - Valider les préchargements",
  consolidation_geotab_fill: "Consolidation - Ajouter les heures Geotab",
  consolidation_geotab_decision: "Consolidation - Décision sur les heures Geotab",
  consolidation_driver_open: "Consolidation - Ouverture d’un chauffeur",
  consolidation_driver_answer: "Consolidation - Qui a conduit ?",
  consolidation_loading_day: "Consolidation - Ce jour / La veille",
  consolidation_dispatch_accept: "Consolidation - Accepter la modification du Dispatch",
  product_compartment_fill: "Expansion des produits",
  compartment_sharing: "Compléter un compartiment",
  dispatch_driver_change: "Changement de chauffeur",
  dispatch_tractor_change: "Changement de camion",
  dispatch_trailer_change: "Changement de remorque",
  data_manager_save_truck: "Data Manager - Camion Sauvegarde",
  data_manager_save_driver: "Data Manager - Chauffeur Sauvegarde",
  data_manager_save_client: "Clients",
  data_manager_save_order: "Commandes",
  source_changes_reject_loading_constraints: "Rejet meunerie",
  disable_truck_dispatch: "Désactivation camion",
  end_of_day_load_assignment: "Chargé PM",
  end_of_day_load_removal: "Retrait PM",
  evening_standard_transfer: "Transfert Jour / Soir",
  gantt_source_backed_save: "Modification commande · Gantt",
	move_delivery: "Déplacement",
	move_delivery_group: "Déplacement de groupe",
	move_order: "Déplacement de commande",
	move_truck_orders: "Déplacement camion",
	optimization: "Optimiser",
	optimize_truck_routes: "Optimiser ce camion",
	planning_reset: "Réinitialiser",
  preloaded_previous_day: "Preload",
  preloaded_previous_day_assignment: "Preload",
  reactivate: "Réactivation",
  recalculate_truck_route: "Recalculer la route",
  release: "Libération",
  release_delivery_group: "Libération de groupe",
  remove_preloaded_previous_day: "Retrait Preload",
  remove_preloaded_previous_day_assignment: "Retrait Preload",
  restore_dispatch_snapshot: "Restauration de sauvegarde",
	segment_reorder: "Réordonnancement",
	source_changes_import_suspendable_errors: "Nouvelles commandes en erreur vers Suspendues",
  source_changes_import_new: "Nouvelles commandes vers Suspendues",
  source_changes_open: "Ouverture Mise à jour",
  source_changes_resume: "Relance Mise à jour",
  source_changes_refresh_loading_constraints: "Actualiser meunerie",
  source_changes_refresh_trucks: "Mise à jour - Actualiser camion",
  source_changes_read: "Lecture des mises à jour",
  source_changes_reject_truck: "Rejet camion",
  source_changes_recover_truck: "Actualiser camion avec suspension",
  source_changes_acknowledge_protected: "Mise à jour - Commande protégée - Conserver Dispatch",
  source_changes_restore_executions: "Restauration exécutions",
  source_changes_remove_orphan_end_of_day_loads: "Retrait Chargé PM orphelin",
  source_changes_resolve_end_of_day_load_integrity: "Résolution Chargé PM",
  source_changes_repair_non_renderable_trip: "Réparation tournée",
  source_changes_suspend_non_renderable_trip: "Suspension tournée incomplète",
	source_changes_remove_date_changed: "Dates déplacées",
	source_changes_remove_deleted: "Retrait données supprimées",
	source_changes_remove_modified: "Commandes modifiées vers Suspendues",
  suspend: "Suspension",
  suspend_delivery_group: "Suspension de groupe",
  suspend_from_error: "Suspension depuis erreur",
};

export function formatPlanningDiagnosticActionLabel(action = "") {
  const normalized = normalizeInlineText(action);
  if (!normalized) {
    return "";
  }
  return REASSIGNMENT_ACTION_LABELS[normalized] || normalized;
}

function formatTruckTimeSettingsActionSuffix({
  hasCapacityTons = false,
  hasMaxWorkHours = false,
  hasStartTime = false,
} = {}) {
  const parts = [];
  if (hasMaxWorkHours) {
    parts.push("Heure max");
  }
  if (hasStartTime) {
    parts.push(parts.length ? "heure de début" : "Heure de début");
  }
  if (hasCapacityTons) {
    parts.push(parts.length ? "capacité" : "Capacité");
  }
  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1) {
    return parts[0];
  }
  if (parts.length === 2) {
    return `${parts[0]} + ${parts[1]}`;
  }
  return `${parts[0]} + ${parts[1]} + ${parts[2]}`;
}

function resolveTruckRouteOptimizationActionSuffix(diagnostics = {}) {
  const localPatchDetails = diagnostics.local_patch?.details;
  const optimizationQuality = diagnostics.optimization_quality
    && typeof diagnostics.optimization_quality === "object"
    && !Array.isArray(diagnostics.optimization_quality)
    ? diagnostics.optimization_quality
    : localPatchDetails?.optimization_quality
      && typeof localPatchDetails.optimization_quality === "object"
      && !Array.isArray(localPatchDetails.optimization_quality)
      ? localPatchDetails.optimization_quality
      : {};
  const requiredMaxWorkHours = Number(optimizationQuality.required_max_work_hours);
  const rawRequiredCapacityTons = optimizationQuality.required_capacity_tons;
  const requiredCapacityTons = Number(rawRequiredCapacityTons);
  const suffix = formatTruckTimeSettingsActionSuffix({
    hasCapacityTons: rawRequiredCapacityTons != null && rawRequiredCapacityTons !== ""
      && Number.isFinite(requiredCapacityTons)
      && requiredCapacityTons >= 0,
    hasMaxWorkHours: Number.isFinite(requiredMaxWorkHours) && requiredMaxWorkHours > 0,
    hasStartTime: Boolean(normalizeInlineText(optimizationQuality.required_start_time)),
  });
  if (suffix) {
    return `Via menu Paramètres chauffeur et camion - ${suffix}`;
  }

  const reason = normalizeInlineText(optimizationQuality.reason).toLowerCase();
  if (reason === "authoritative_start_time_applied") {
    return "Via menu Paramètres chauffeur et camion - Heure de début";
  }
  if (reason === "authoritative_time_settings_applied") {
    return "Via menu Paramètres chauffeur et camion";
  }
  return "Clic droit";
}

function resolveTruckRefreshActionSuffix(diagnostics = {}) {
  let hasCapacityTons = false;
  let hasMaxWorkHours = false;
  let hasStartTime = false;
  const details = Array.isArray(diagnostics.truck_refresh_details)
    ? diagnostics.truck_refresh_details
    : [];
  details.forEach((detail) => {
    const changedFields = Array.isArray(detail?.changed_fields) ? detail.changed_fields : [];
    changedFields.forEach((field) => {
      const fieldName = normalizeInlineText(field?.field).toLowerCase();
      hasCapacityTons = hasCapacityTons || fieldName === "capacity_tons";
      hasMaxWorkHours = hasMaxWorkHours || fieldName === "max_work_hours";
      hasStartTime = hasStartTime || fieldName === "start_time";
    });
  });
  return formatTruckTimeSettingsActionSuffix({ hasCapacityTons, hasMaxWorkHours, hasStartTime });
}

function resolvePeriodActionLabel(action, item, diagnostics) {
  const periodLabel = (period) => period === "evening" ? "Soir" : period === "day" ? "Jour" : "";
  const period = item.dispatchPeriod || item.dispatch_period || diagnostics.dispatch_period;
  if (action === "optimization") {
    return periodLabel(period) ? `Optimiser ${periodLabel(period)}` : "";
  }
  if (!["evening_standard_transfer", "reactivate", "suspend", "suspend_delivery_group"].includes(action)) return "";
  const transfer = diagnostics.evening_standard_transfer || {};
  const changes = Array.isArray(diagnostics.movement_changes) ? diagnostics.movement_changes : [];
  const position = (side) => {
    const positions = changes.map((change) => change?.[side]).filter(Boolean);
    const uniqueValue = (key) => {
      const values = [...new Set(positions.map((value) => value[key]).filter(Boolean))];
      return values.length === 1 ? values[0] : "";
    };
    const location = diagnostics[`${side}_location`] || uniqueValue("location");
    const dispatchPeriod = diagnostics[`${side}_dispatch_period`]
      || transfer[`${side}_dispatch_period`] || uniqueValue("dispatch_period")
      || (action !== "evening_standard_transfer" ? period : "");
    const label = location === "suspended" ? "Suspendues" : location === "assigned" ? "Dispatch" : "";
    return label && periodLabel(dispatchPeriod) ? `${label} ${periodLabel(dispatchPeriod)}` : "";
  };
  const source = position("source");
  const target = position("target");
  return source && target ? `${source} → ${target}` : "";
}

export function formatReassignmentHistoryActionLabel(item = {}) {
  const orderEdit = buildPlanningOrderEditPresentation(item);
  if (orderEdit) return orderEdit.title;
  const diagnostics = item?.diagnostics && typeof item.diagnostics === "object" && !Array.isArray(item.diagnostics)
    ? item.diagnostics
    : {};
  const action = normalizeInlineText(item?.operationAction)
    || normalizeInlineText(item?.operation_action)
    || normalizeInlineText(diagnostics.operation_action)
    || normalizeInlineText(item?.action);
  const feedback = diagnostics.operation_feedback;
  if (["data_manager_save_client", "data_manager_save_order"].includes(action)) {
    const isNew = feedback?.mutation_kind === "create";
    if (action === "data_manager_save_client") return isNew ? "Nouveau client" : "Client existant";
    return isNew ? (feedback.resource_count > 1 ? "Nouvelles commandes" : "Nouvelle commande") : "Commande existante";
  }
  if (action === "data_manager_save_truck" && (
    feedback?.origin === "data_manager_compartments"
    || (!feedback?.origin && feedback?.changes?.some((audit) => audit.entity_kind === "compartment"
      || audit.field_changes?.some((change) => change.field === "source_label" && /compartments/i.test(String(change.old_value)))))
  )) {
    return "Data Manager - Remorques Sauvegarde";
  }
  const actionLabel = resolvePeriodActionLabel(action, item, diagnostics) || ((action.startsWith("source_changes_") || action.startsWith("data_manager_"))
    ? formatPlanningDiagnosticActionLabel(action)
    : normalizeInlineText(item?.operationActionLabel)
      || formatPlanningDiagnosticActionLabel(item?.action));
  const actionSuffix = diagnostics.compartment_sharing?.decision === "share" ? "Compléter un compartiment"
    : action === "optimize_truck_routes"
    ? resolveTruckRouteOptimizationActionSuffix(diagnostics)
    : action === "source_changes_refresh_trucks"
      ? resolveTruckRefreshActionSuffix(diagnostics)
      : "";
  const isTimeSettingsMenuAction = action === "optimize_truck_routes"
    && actionSuffix.startsWith("Via menu Paramètres chauffeur et camion");
  const detailedActionLabel = isTimeSettingsMenuAction
    ? actionSuffix
    : actionSuffix && actionLabel
      ? `${actionLabel} · ${actionSuffix}`
      : actionLabel;
  const isUndo = normalizeInlineText(diagnostics.interaction_origin).toLowerCase() === "undo";
  if (!isUndo) {
    return detailedActionLabel;
  }
  return detailedActionLabel ? `Revenir · ${detailedActionLabel}` : "Revenir";
}

export function listPlanningDiagnosticActionOptions(items = [], activityScope = "dispatch") {
  const seen = new Set();
  const options = [];
  const actions = [
    ...(activityScope === "consolidation" ? Object.keys(REASSIGNMENT_ACTION_LABELS).filter((action) => action.startsWith("consolidation_"))
      : activityScope === "source_changes" ? [] : ["optimization", "planning_reset"]),
    ...(Array.isArray(items) ? items.map((item) => item?.action) : []),
  ];
  actions.forEach((rawAction) => {
    const action = normalizeInlineText(rawAction);
    if (!action || seen.has(action)) {
      return;
    }
    seen.add(action);
    options.push({
      label: formatPlanningDiagnosticActionLabel(action),
      value: action,
    });
  });
  return options.sort((left, right) => left.label.localeCompare(right.label, "fr-CA", { sensitivity: "base" }));
}

function normalizeStagesMs(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const stages = {};
  Object.entries(value).forEach(([key, rawValue]) => {
    const stageKey = normalizeInlineText(key);
    const stageMs = Number(rawValue);
    if (!stageKey || !Number.isFinite(stageMs)) {
      return;
    }
    stages[stageKey] = stageMs;
  });
  return stages;
}

function resolveHistoryActorLabel(item = {}) {
  const context = item?.context && typeof item.context === "object" && !Array.isArray(item.context)
    ? item.context
    : {};
  const diagnostics = item?.diagnostics && typeof item.diagnostics === "object" && !Array.isArray(item.diagnostics)
    ? item.diagnostics
    : {};
  return normalizeInlineText(item?.actorLabel)
    || normalizeInlineText(item?.actor_label)
    || normalizeInlineText(context.actor_label)
    || normalizeInlineText(diagnostics.actor_label)
    || normalizeInlineText(diagnostics.actor_name);
}

function resolveHistoryActorUserId(item = {}) {
  const context = item?.context && typeof item.context === "object" && !Array.isArray(item.context)
    ? item.context
    : {};
  const diagnostics = item?.diagnostics && typeof item.diagnostics === "object" && !Array.isArray(item.diagnostics)
    ? item.diagnostics
    : {};
  return normalizeInlineText(item?.actorUserId)
    || normalizeInlineText(item?.actor_user_id)
    || normalizeInlineText(context.actor_user_id)
    || normalizeInlineText(diagnostics.actor_user_id);
}

function normalizeHistoryItem(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    return null;
  }
  const diagnostics = item.diagnostics && typeof item.diagnostics === "object" && !Array.isArray(item.diagnostics)
    ? item.diagnostics
    : null;
  const operationAction = normalizeInlineText(item.operation_action)
    || normalizeInlineText(diagnostics?.operation_action);
  const operationActionLabel = normalizeInlineText(item.operation_action_label)
    || normalizeInlineText(diagnostics?.operation_action_label);
  const feedback = diagnostics?.operation_feedback;
  const totalMs = Number(feedback?.client_total_ms ?? item.total_ms ?? diagnostics?.operation_total_ms);
  const status = normalizeInlineText(feedback?.status ?? item.status) || "success";
  const kind = normalizeInlineText(item.kind) || "reassignment";
  const action = operationAction
    || normalizeInlineText(item.action)
    || (kind === "run" ? "optimization" : "");
  return {
    action,
    actorLabel: resolveHistoryActorLabel(item),
    actorUserId: resolveHistoryActorUserId(item),
    activityScope: item.activity_scope,
    dispatchDate: normalizeInlineText(feedback ? feedback.dispatch_date : item.dispatch_date).slice(0, 10),
    dispatchPeriods: feedback?.dispatch_periods,
    dispatchPeriod: feedback ? (feedback.dispatch_periods?.[0] || "") : normalizePlanningLastDiagnosticsPeriod(item.dispatch_period),
    kind,
    captureKind: normalizeInlineText(item.capture_kind) || normalizeInlineText(diagnostics?.capture_kind),
    operationAction,
    operationActionLabel,
    operationEventId: normalizeInlineText(item.operation_event_id)
      || normalizeInlineText(diagnostics?.operation_event_id),
    recordedAt: normalizeInlineText(item.recorded_at),
    stagesMs: normalizeStagesMs(item.stages_ms),
    status,
    statusLabel: feedback?.consolidation_context?.outcome === "save_unconfirmed" ? "Sauvegarde non confirmée"
      : feedback && status === "refused" ? "Bloqué" : formatPlanningDiagnosticStatusLabel(status),
    statusTone: formatPlanningDiagnosticStatusTone(status),
    totalMs: Number.isFinite(totalMs) ? totalMs : null,
    diagnostics,
    context: item.context && typeof item.context === "object" && !Array.isArray(item.context)
      ? item.context
      : null,
  };
}

function formatDiagnosticDuration(value) {
  const duration = Number(value);
  if (!Number.isFinite(duration)) {
    return "n/d";
  }
  if (duration >= 60000) {
    const totalSeconds = Math.round(duration / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
  }
  if (duration >= 1000) {
    return `${(duration / 1000).toLocaleString("fr-CA", { maximumFractionDigits: 2 })} s`;
  }
  return `${duration.toLocaleString("fr-CA", { maximumFractionDigits: 1 })} ms`;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }
  return null;
}

function sumFiniteNumbers(...values) {
  const finiteValues = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 0);
  return finiteValues.length
    ? finiteValues.reduce((total, value) => total + value, 0)
    : null;
}

function resolveRunHistoryDiagnostics(item = {}) {
  const diagnostics = item?.diagnostics && typeof item.diagnostics === "object" && !Array.isArray(item.diagnostics)
    ? item.diagnostics
    : {};
  const optimization = diagnostics.optimization
    && typeof diagnostics.optimization === "object"
    && !Array.isArray(diagnostics.optimization)
    ? diagnostics.optimization
    : diagnostics;
  return { diagnostics, optimization };
}

function resolveRunHistoryStageMs(item, diagnostics, stageName) {
  return firstFiniteNumber(
    diagnostics?.stages_ms?.[stageName],
    item?.stagesMs?.[stageName],
  );
}

function formatProductTimingLabel(value) {
  const productKey = normalizeInlineText(value);
  if (!productKey) {
    return "";
  }
  return `${productKey.charAt(0).toLocaleUpperCase("fr-CA")}${productKey.slice(1)}`;
}

export function buildRunHistoryCardMetrics(item = {}) {
  if (item?.kind !== "run") {
    return [];
  }
  const { diagnostics, optimization } = resolveRunHistoryDiagnostics(item);
  const performance = optimization?.performance_summary && typeof optimization.performance_summary === "object"
    ? optimization.performance_summary
    : {};
  const reference = optimization?.reference_metrics && typeof optimization.reference_metrics === "object"
    ? optimization.reference_metrics
    : {};
  const solver = optimization?.solver_breakdown && typeof optimization.solver_breakdown === "object"
    ? optimization.solver_breakdown
    : {};
  const routing = optimization?.routing && typeof optimization.routing === "object"
    ? optimization.routing
    : {};
  const sequenceCache = optimization?.compute_cache?.sequence_simulations
    && typeof optimization.compute_cache.sequence_simulations === "object"
    ? optimization.compute_cache.sequence_simulations
    : {};
  const isPlanningReset = item.action === "planning_reset";
  const totalBackendMs = firstFiniteNumber(
    item.totalMs,
    diagnostics.total_backend_ms,
    diagnostics.run_total_ms,
    optimization.total_backend_ms,
    optimization.run_total_ms,
  );
  const optimizationMs = isPlanningReset
    ? firstFiniteNumber(
      optimization.total_backend_ms,
      optimization.run_total_ms,
      resolveRunHistoryStageMs(item, diagnostics, "execute_optimization_request"),
    )
    : null;
  const solverMs = firstFiniteNumber(
    performance.solver_ms,
    resolveRunHistoryStageMs(item, optimization, "optimize_orders_to_trucks"),
  );
  const cohesionMs = firstFiniteNumber(
    reference.validate_client_cohesion_ms,
    resolveRunHistoryStageMs(item, optimization, "validate_client_cohesion"),
  );
  const ortoolsMs = firstFiniteNumber(solver.ortools_total_ms, solver.engine_solve_ms);
  const tripBuildingMs = firstFiniteNumber(solver.trip_building_ms);
  const localSolverMs = firstFiniteNumber(reference.local_solver_ms);
  const transformationMs = firstFiniteNumber(
    performance.transformation_ms,
    resolveRunHistoryStageMs(item, optimization, "build_optimization_view"),
  );
  const persistenceMs = firstFiniteNumber(
    isPlanningReset ? resolveRunHistoryStageMs(item, diagnostics, "persist_reset_runtime_bundle") : null,
    performance.persistence_ms,
    resolveRunHistoryStageMs(item, optimization, "store_latest_optimization"),
  );
  const routeEnrichmentMs = firstFiniteNumber(
    performance.route_enrichment_ms,
    resolveRunHistoryStageMs(item, optimization, "enrich_result_with_actual_routes"),
  );
  const routeClientMs = sumFiniteNumbers(
    routing.here_request_ms,
    routing.cache_lookup_ms,
    routing.cache_write_ms,
    routing.geocode_provider_request_ms,
  );
  const routeMs = routeClientMs !== null && routeEnrichmentMs !== null
    ? Math.max(routeClientMs, routeEnrichmentMs)
    : firstFiniteNumber(routeClientMs, routeEnrichmentMs);
  const sqlMs = firstFiniteNumber(performance.sql_total_ms, optimization.sql_total_ms);
  const sqlCount = firstFiniteNumber(performance.sql_query_count, optimization.sql_query_count);
  const cpuCoreEquivalent = firstFiniteNumber(
    performance.thread_cpu_core_equivalent,
    performance.process_cpu_core_equivalent,
    optimization.compute_runtime?.thread_cpu_core_equivalent,
    optimization.compute_runtime?.process_cpu_core_equivalent,
  );
  const sequenceCacheHits = firstFiniteNumber(sequenceCache.hits);
  const sequenceCacheMisses = firstFiniteNumber(sequenceCache.misses);
  const isOptimization = item.action === "optimization";
  const inputOrderCount = isOptimization
    ? firstFiniteNumber(optimization.input_order_count, diagnostics.input_order_count)
    : null;
  const progressStepTimings = optimization?.progress_step_timings_ms
    && typeof optimization.progress_step_timings_ms === "object"
    && !Array.isArray(optimization.progress_step_timings_ms)
    ? optimization.progress_step_timings_ms
    : {};
  const assignmentsMs = isOptimization
    ? firstFiniteNumber(progressStepTimings.assignments)
    : null;
  const dispatchImprovementMs = isOptimization
    ? firstFiniteNumber(progressStepTimings.dispatch_improvement)
    : null;
  const rawProductSolverTimings = isOptimization && Array.isArray(optimization?.product_solver_timings)
    ? optimization.product_solver_timings
    : [];
  const productSolverMetrics = rawProductSolverTimings.map((rawTiming, index) => {
    if (!rawTiming || typeof rawTiming !== "object" || Array.isArray(rawTiming)) {
      return null;
    }
    const productLabel = formatProductTimingLabel(rawTiming.product_key);
    const elapsedMs = Number(rawTiming.elapsed_ms);
    const budgetMs = Number(rawTiming.budget_ms);
    if (!productLabel || !Number.isFinite(elapsedMs) || elapsedMs < 0) {
      return null;
    }
    return {
      key: `run-product-solver-${normalizeInlineText(rawTiming.product_key) || index}`,
      label: `Produit · ${productLabel}`,
      value: [
        formatDiagnosticDuration(elapsedMs),
        Number.isFinite(budgetMs) && budgetMs >= 0 ? formatDiagnosticDuration(budgetMs) : "",
      ].filter(Boolean).join(" / "),
    };
  }).filter(Boolean);
  return [
    totalBackendMs !== null ? {
      key: "run-total-backend",
      label: "Total backend",
      value: formatDiagnosticDuration(totalBackendMs),
    } : null,
    inputOrderCount !== null && inputOrderCount >= 0 ? {
      key: "run-input-order-count",
      label: "Commandes au clic",
      value: Math.trunc(inputOrderCount).toLocaleString("fr-CA"),
    } : null,
    assignmentsMs !== null ? {
      key: "run-assignments",
      label: "Calcul des affectations",
      value: formatDiagnosticDuration(assignmentsMs),
    } : null,
    dispatchImprovementMs !== null ? {
      key: "run-dispatch-improvement",
      label: "Amélioration du Dispatch",
      value: formatDiagnosticDuration(dispatchImprovementMs),
    } : null,
    ...productSolverMetrics,
    optimizationMs !== null ? {
      key: "run-reset-optimization",
      label: "Optimisation incluse",
      value: formatDiagnosticDuration(optimizationMs),
    } : null,
    solverMs !== null ? {
      key: "run-solver",
      label: "Solveur",
      value: formatDiagnosticDuration(solverMs),
    } : null,
    cohesionMs !== null ? {
      key: "run-client-cohesion",
      label: "Cohésion client",
      value: formatDiagnosticDuration(cohesionMs),
    } : null,
    localSolverMs !== null ? {
      key: "run-local-solver",
      label: "Solveurs locaux",
      value: formatDiagnosticDuration(localSolverMs),
    } : null,
    ortoolsMs !== null ? {
      key: "run-ortools",
      label: "OR-Tools",
      value: formatDiagnosticDuration(ortoolsMs),
    } : null,
    tripBuildingMs !== null ? {
      key: "run-trip-building",
      label: "Construction tournées",
      value: formatDiagnosticDuration(tripBuildingMs),
    } : null,
    transformationMs !== null ? {
      key: "run-transformation",
      label: "Vue / transformation",
      value: formatDiagnosticDuration(transformationMs),
    } : null,
    persistenceMs !== null ? {
      key: "run-persistence",
      label: "Persistance",
      value: formatDiagnosticDuration(persistenceMs),
    } : null,
    routeMs !== null ? {
      key: "run-routing",
      label: "Routage / HERE",
      value: formatDiagnosticDuration(routeMs),
    } : null,
    sqlMs !== null || sqlCount !== null ? {
      key: "run-sql",
      label: "SQL",
      value: [
        sqlCount !== null ? `${sqlCount.toLocaleString("fr-CA")} req.` : "",
        sqlMs !== null ? formatDiagnosticDuration(sqlMs) : "",
      ].filter(Boolean).join(" · "),
    } : null,
    sequenceCacheHits !== null || sequenceCacheMisses !== null ? {
      key: "run-compute-cache",
      label: "Cache simulations",
      value: [
        sequenceCacheHits !== null ? `${sequenceCacheHits.toLocaleString("fr-CA")} hits` : "",
        sequenceCacheMisses !== null ? `${sequenceCacheMisses.toLocaleString("fr-CA")} misses` : "",
      ].filter(Boolean).join(" · "),
    } : null,
    cpuCoreEquivalent !== null ? {
      key: "run-cpu-core-equivalent",
      label: "CPU équivalent",
      value: `${cpuCoreEquivalent.toLocaleString("fr-CA", { maximumFractionDigits: 2 })} cœur`,
    } : null,
  ].filter(Boolean);
}

export function buildSourceChangesPerformanceCardMetrics(item = {}) {
  const diagnostics = item?.diagnostics && typeof item.diagnostics === "object" && !Array.isArray(item.diagnostics)
    ? item.diagnostics
    : {};
  if (!diagnostics.operation_feedback && (item?.captureKind || diagnostics.capture_kind) !== "source_changes_performance") {
    return [];
  }
  const operationTotalMs = Number(diagnostics.operation_feedback?.client_total_ms ?? diagnostics.operation_total_ms ?? item?.totalMs);
  const serverWorkMs = Number(diagnostics.server_work_ms);
  const requestCount = Number(diagnostics.request_count);
  const affectedOrderCount = Number(diagnostics.affected_order_count);
  const hasExpectedUnavailableEveningSnapshot = (
    Array.isArray(diagnostics.request_metrics) ? diagnostics.request_metrics : []
  ).some((requestMetric) => (
    requestMetric
    && typeof requestMetric === "object"
    && requestMetric.expected_unavailable === true
    && normalizeInlineText(requestMetric.reason_code) === SOURCE_CHANGES_SNAPSHOT_UNAVAILABLE_REASON_CODE
    && normalizePlanningLastDiagnosticsPeriod(requestMetric.dispatch_period) === "evening"
  ));
  const truckRefreshMetrics = buildTruckRefreshDiagnosticContextItems(item, diagnostics).map((contextItem) => ({
    key: `source-changes-${contextItem.key}`,
    label: contextItem.label,
    note: contextItem.detail,
    noteWrap: contextItem.detailWrap === true,
    value: contextItem.value,
  }));
  return [
    {
      key: "source-changes-operation-total",
      label: "Temps global",
      tone: "accent",
      value: formatDiagnosticDuration(operationTotalMs),
    },
    ...truckRefreshMetrics,
    Number.isFinite(serverWorkMs) ? {
      key: "source-changes-server-work",
      label: "Travail serveur",
      value: formatDiagnosticDuration(serverWorkMs),
    } : null,
    // Changement du Gantt complété par le navigateur : sa requête n'est pas comptée ici, « 0 » serait trompeur.
    Number.isFinite(requestCount) && diagnostics.operation_feedback?.source !== "server_transaction" ? {
      key: "source-changes-request-count",
      label: "Traitements serveur",
      value: requestCount.toLocaleString("fr-CA"),
    } : null,
    hasExpectedUnavailableEveningSnapshot ? {
      key: "source-changes-evening-snapshot",
      label: "Planning Soir",
      value: "Non applicable",
    } : null,
    Number.isFinite(affectedOrderCount) && affectedOrderCount >= 0 ? {
      key: "source-changes-order-count",
      label: "Commandes",
      value: affectedOrderCount.toLocaleString("fr-CA"),
    } : null,
  ].filter(Boolean);
}

export function normalizeReassignmentDiagnosticsHistoryPayload(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return { items: [], total: 0 };
  }
  const items = Array.isArray(payload.items)
    ? payload.items.map(normalizeHistoryItem).filter(Boolean)
    : [];
  const total = Number(payload.total);
  return {
    dispatchDate: normalizeInlineText(payload.dispatch_date).slice(0, 10),
    dispatchPeriod: payload.dispatch_period
      ? normalizePlanningLastDiagnosticsPeriod(payload.dispatch_period)
      : "",
    items,
    recordedAt: normalizeInlineText(payload.recorded_at),
    status: normalizeInlineText(payload.status),
    total: Number.isFinite(total) ? total : items.length,
  };
}

function getEndOfDayLoadMovementMarkers(item, side) {
  const changes = item?.diagnostics?.movement_changes;
  return (Array.isArray(changes) ? changes : [])
    .map((change) => change?.[side]?.end_of_day_load)
    .filter((marker) => marker && typeof marker === "object" && !Array.isArray(marker));
}

function getReassignmentHistoryDispatchDates(item) {
  const markers = [
    ...getEndOfDayLoadMovementMarkers(item, "source"),
    ...getEndOfDayLoadMovementMarkers(item, "target"),
  ];
  return new Set([
    item?.dispatchDate,
    ...markers.flatMap((marker) => [marker.marker_dispatch_date, marker.source_dispatch_date, marker.target_dispatch_date]),
  ].map((value) => normalizeInlineText(value).slice(0, 10)).filter(Boolean));
}

function buildEndOfDayLoadDateItems(item) {
  const targetMarkers = getEndOfDayLoadMovementMarkers(item, "target");
  const markers = targetMarkers.length ? targetMarkers : getEndOfDayLoadMovementMarkers(item, "source");
  const origin = { dispatch: "Gantt", data_manager: "Data Manager" }[item?.diagnostics?.origin];
  return [
    ...(origin ? [{ key: "pm-origin", label: "Origine", value: origin, detail: "" }] : []),
    ...[["source_dispatch_date", "Date source PM"], ["target_dispatch_date", "Date cible PM"]]
    .flatMap(([field, label]) => {
      const dates = [...new Set(markers.map((marker) => normalizeInlineText(marker[field]).slice(0, 10)).filter(Boolean))];
      return dates.length ? [{
        key: `pm-${field}`,
        label,
        value: dates.map(formatPlanningDiagnosticDispatchDate).join(", "),
        detail: dates.join(", "),
      }] : [];
    }),
  ];
}

export function filterReassignmentDiagnosticHistoryItems(items = [], {
  action = "",
  dispatchDate = "",
  dispatchPeriod = "",
  status = "",
} = {}) {
  const normalizedAction = normalizeInlineText(action);
  const normalizedDate = normalizeInlineText(dispatchDate).slice(0, 10);
  const normalizedPeriod = normalizeInlineText(dispatchPeriod);
  const normalizedStatus = normalizeInlineText(status).toLowerCase();
  return (Array.isArray(items) ? items : []).filter((item) => {
    if (normalizedAction && item?.action !== normalizedAction) {
      return false;
    }
    if (normalizedDate && !getReassignmentHistoryDispatchDates(item).has(normalizedDate)) {
      return false;
    }
    const movementChanges = item?.diagnostics?.movement_changes;
    const movementPeriods = (Array.isArray(movementChanges) ? movementChanges : []).flatMap((change) => [
      change?.source?.dispatch_period, change?.target?.dispatch_period,
    ]);
    if (normalizedPeriod && item?.dispatchPeriod !== normalizedPeriod && !movementPeriods.includes(normalizedPeriod)) {
      return false;
    }
    if (normalizedStatus && (item?.status || "success") !== normalizedStatus) {
      return false;
    }
    return true;
  });
}

function formatMetricCopyLine(metric) {
  if (!metric || typeof metric !== "object") {
    return "";
  }
  const label = normalizeInlineText(metric.label);
  const value = normalizeInlineText(metric.value);
  if (!label && !value) {
    return "";
  }
  return `- ${label || "Métrique"}: ${value || "n/d"}`;
}

function buildRunHistoryTimestampItems(item = {}) {
  const recordedAt = normalizeInlineText(item.recordedAt);
  const parsedRecordedAt = recordedAt ? new Date(recordedAt) : null;
  const hasRecordedAt = parsedRecordedAt && !Number.isNaN(parsedRecordedAt.getTime());
  const dispatchDateLabel = formatPlanningDiagnosticDispatchDate(item.dispatchDate);
  return [
    hasRecordedAt ? {
      key: "performed-date",
      label: "Date",
      value: new Intl.DateTimeFormat("fr-CA", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(parsedRecordedAt),
      detail: "",
    } : null,
    hasRecordedAt ? {
      key: "performed-time",
      label: "Heure",
      value: [
        parsedRecordedAt.getHours(),
        parsedRecordedAt.getMinutes(),
        parsedRecordedAt.getSeconds(),
      ].map((part) => String(part).padStart(2, "0")).join(":"),
      detail: "",
    } : null,
    dispatchDateLabel ? {
      key: "dispatch-date",
      label: "Dispatch",
      value: dispatchDateLabel,
      detail: item.dispatchDate || "",
    } : null,
    item.actorLabel ? {
      key: "actor",
      label: "Utilisateur",
      value: item.actorLabel,
      detail: "",
    } : null,
  ].filter(Boolean);
}

function formatTruckRefreshDiagnosticValue(fieldName, value) {
  const formatted = formatDataManagerAuditSafeFieldValue(fieldName, value);
  if (formatted) {
    return formatted;
  }
  if (value == null || value === "") {
    return "Aucune";
  }
  if (typeof value === "boolean") {
    return value ? "Oui" : "Non";
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "Aucune";
  }
  return typeof value === "object" ? "Valeur structurée" : String(value);
}

function buildTruckRefreshDiagnosticContextItems(item = {}, diagnostics = {}) {
  const operationAction = normalizeInlineText(diagnostics.operation_action)
    || normalizeInlineText(item?.operationAction)
    || normalizeInlineText(item?.action);
  const details = diagnostics.truck_refresh_details;
  if (
    !["source_changes_refresh_trucks", "source_changes_recover_truck"].includes(operationAction)
    || !Array.isArray(details)
  ) {
    return [];
  }
  return details.map((detail, detailIndex) => {
    if (!detail || typeof detail !== "object" || Array.isArray(detail)) {
      return null;
    }
    const truckId = normalizeInlineText(detail.truck_id);
    const truckLabel = normalizeInlineText(detail.truck_label) || truckId;
    if (!truckLabel) {
      return null;
    }
    const transitionLines = (Array.isArray(detail.changed_fields) ? detail.changed_fields : [])
      .map((field) => {
        if (!field || typeof field !== "object" || Array.isArray(field)) {
          return "";
        }
        const fieldName = normalizeInlineText(field.field).toLowerCase();
        if (!fieldName) {
          return "";
        }
        const label = formatDataManagerAuditFieldLabel(fieldName, {
          fallbackLabel: normalizeInlineText(field.label),
        });
        if (normalizeInlineText(field.value_visibility).toLowerCase() !== "visible") {
          return `${label} : Valeur masquée`;
        }
        return `${label} : ${formatTruckRefreshDiagnosticValue(fieldName, field.before)} → ${formatTruckRefreshDiagnosticValue(fieldName, field.after)}`;
      })
      .filter(Boolean);
    return {
      key: `truck-refresh-${truckId || detailIndex}`,
      label: "Camion",
      value: truckLabel,
      detail: transitionLines.join("\n") || "Modification non détaillée",
      detailWrap: true,
    };
  }).filter(Boolean);
}

export function buildPlanningDiagnosticCopyText({
  history = [],
  kind = "run",
  metrics = [],
  scope = null,
  status = "success",
} = {}) {
  const kindLabel = kind === "reassignment" ? "Diagnostic réaffectation" : "Diagnostic run";
  const scopeLabel = formatPlanningDiagnosticScopeLabel(scope || {});
  const statusLabel = formatPlanningDiagnosticStatusLabel(status);
  const metricLines = (Array.isArray(metrics) ? metrics : [])
    .map(formatMetricCopyLine)
    .filter(Boolean);
  const historyLines = (Array.isArray(history) ? history : []).slice(0, 8).map((item) => {
    const itemScope = formatPlanningDiagnosticScopeLabel(item);
    const total = Number.isFinite(item?.totalMs) ? `${item.totalMs} ms` : "n/d";
    const action = normalizeInlineText(item?.action);
    return `- ${itemScope}${action ? ` · ${action}` : ""}: ${total}`;
  });
  return [
    `# ${kindLabel}`,
    scopeLabel ? `- Périmètre: ${scopeLabel}` : "",
    `- Statut: ${statusLabel}`,
    scope?.recordedAt ? `- Enregistré: ${scope.recordedAt}` : "",
    metricLines.length ? "" : "",
    metricLines.length ? "## Métriques" : "",
    ...metricLines,
    historyLines.length ? "" : "",
    historyLines.length ? "## Historique récent" : "",
    ...historyLines,
  ].filter((line, index, lines) => line !== "" || lines[index - 1] !== "").join("\n").trim();
}

export function buildReassignmentHistoryDiagnosticPanel(item = {}) {
  const diagnostics = item?.diagnostics && typeof item.diagnostics === "object" && !Array.isArray(item.diagnostics)
    ? item.diagnostics
    : {};
  const context = item?.context && typeof item.context === "object" && !Array.isArray(item.context)
    ? item.context
    : {};
  const panel = buildReassignmentDiagnosticPanel({
    captured_at: item?.recordedAt || "",
    dispatch_date: item?.dispatchDate || "",
    actor_label: item?.actorLabel || "",
    last_reassignment_diagnostics: {
      ...context,
      ...diagnostics,
      action: diagnostics.operation_action || item?.operationAction || diagnostics.action || item?.action || "",
      actor_label: diagnostics.actor_label || context.actor_label || item?.actorLabel || "",
      actor_user_id: diagnostics.actor_user_id || context.actor_user_id || item?.actorUserId || "",
      recorded_at: diagnostics.recorded_at || item?.recordedAt || "",
      stages_ms: diagnostics.stages_ms || item?.stagesMs || {},
      total_backend_ms: diagnostics.total_backend_ms ?? diagnostics.totalBackendMs ?? item?.totalMs,
    },
  });
  let timestampItems = Array.isArray(panel?.timestampItems) && panel.timestampItems.filter(Boolean).length
    ? panel.timestampItems.filter(Boolean)
    : buildRunHistoryTimestampItems(item);
  const feedback = diagnostics.operation_feedback;
  if (feedback?.action === "data_manager_save_order" && feedback.mutation_kind === "create") {
    const dates = [...new Set(feedback.delivery_dates || [])].sort();
    timestampItems = timestampItems.filter((timestampItem) => timestampItem.key !== "dispatch-date");
    const actorIndex = timestampItems.findIndex((timestampItem) => timestampItem.key === "actor");
    timestampItems.splice(actorIndex < 0 ? timestampItems.length : actorIndex, 0, {
      key: "delivery-date",
      label: dates.length > 1 ? "Livraisons" : "Livraison",
      value: dates.length ? dates.map(formatPlanningDiagnosticDispatchDate).join(", ") : "Non renseignée",
      detail: dates.join(", "),
    });
  }
  timestampItems.push(...buildEndOfDayLoadDateItems(item));
  const truckRefreshContextItems = buildTruckRefreshDiagnosticContextItems(item, diagnostics);
  if (item?.kind === "run") {
    return {
      ...panel,
      contextItems: [],
      metrics: buildRunHistoryCardMetrics(item),
      pills: [],
      timestampItems: buildRunHistoryTimestampItems(item),
    };
  }
  if (truckRefreshContextItems.length) {
    return {
      ...(panel || {}),
      contextItems: [],
      metrics: Array.isArray(panel?.metrics) ? panel.metrics : [],
      pills: Array.isArray(panel?.pills)
        ? panel.pills.filter((pill) => pill?.key !== "resolution")
        : [],
      timestampItems,
    };
  }
  if (!panel) {
    return {
      contextItems: [],
      metrics: [],
      pills: [],
      timestampItems,
    };
  }
  return {
    ...panel,
    contextItems: [
      ...(panel.contextItems || []),
      ...(diagnostics.compartment_sharing?.decision === "share" ? [
        { key: "compartment-sharing-confirmation", label: "Confirmation", value: "Compléter un compartiment",
          detail: "Partage du même produit confirmé par l’utilisateur", detailWrap: true },
        ...buildCompartmentSharingConfirmationContent(diagnostics.compartment_sharing).summaryItems.map((entry, index) => ({
          key: `compartment-sharing-${index}`, label: entry.label, value: entry.value,
          detail: [diagnostics.compartment_sharing.target_truck_label || diagnostics.compartment_sharing.target_truck_id,
            diagnostics.compartment_sharing.product?.grain_code, diagnostics.compartment_sharing.product?.grain_type].filter(Boolean).join(" · "),
          detailWrap: true,
        })),
      ] : []),
    ],
    timestampItems,
  };
}

export function buildReassignmentHistoryItemCopyText(item = {}) {
	const panel = buildReassignmentHistoryDiagnosticPanel(item);
	const actionLabel = formatReassignmentHistoryActionLabel(item);
	const panelMetrics = Array.isArray(panel?.metrics) ? panel.metrics.filter(Boolean) : [];
	const deliveryDate = panel?.timestampItems?.find((timestampItem) => timestampItem.key === "delivery-date");
	const isRun = item?.kind === "run";
	const summaryText = buildPlanningDiagnosticCopyText({
		kind: isRun ? "run" : "reassignment",
    metrics: [
      actionLabel ? { label: "Action", value: actionLabel } : null,
      deliveryDate ? { label: deliveryDate.label, value: deliveryDate.value } : null,
      item?.actorLabel ? { label: "Utilisateur", value: item.actorLabel } : null,
      ...panelMetrics,
    ].filter(Boolean),
    scope: deliveryDate ? { ...item, dispatchDate: "", dispatchPeriods: [] } : item,
		status: item?.status || "success",
	});
	const diagnostics = item?.diagnostics && typeof item.diagnostics === "object" && !Array.isArray(item.diagnostics)
		? item.diagnostics
		: {};
	const movementChanges = Array.isArray(diagnostics.movement_changes)
		? diagnostics.movement_changes
		: [];
	const requestMetrics = Array.isArray(diagnostics.request_metrics)
		? diagnostics.request_metrics
		: [];
	const sections = [summaryText];
	if (diagnostics.compartment_sharing?.decision === "share") {
		sections.push(`## Compléter un compartiment — confirmation utilisateur\n${JSON.stringify(diagnostics.compartment_sharing, null, 2)}`);
	}
	if ((diagnostics.operation_action || item?.operationAction || diagnostics.action || item?.action) === "optimize_truck_routes"
		&& diagnostics.solver_execution != null) {
		sections.push(`## Exécution du solveur\n${JSON.stringify({ solver_execution: diagnostics.solver_execution }, null, 2)}`);
	}
	const sequenceCopyText = buildTruckSequenceComparisonCopyText(item);
	if (sequenceCopyText) sections.push(sequenceCopyText);
	const orderEditCopyText = buildPlanningOrderEditCopyText(item);
	if (orderEditCopyText) sections.push(orderEditCopyText);
	const consolidation = buildConsolidationCaptureDetails(diagnostics.operation_feedback);
	if (consolidation) sections.push(["## Geste Consolidation", ...consolidation.tags.map((tag) => `- ${tag}`),
		...consolidation.rows.map((row) => `- ${row.label}: ${row.value}${row.detail ? ` (${row.detail})` : ""}`),
		...consolidation.lists.flatMap((list) => [`- ${list.label}:`, ...list.items.map((entry) => `  - ${entry}`)])].join("\n"));
	if (diagnostics.operation_feedback) sections.push(`## Feedback client et diagnostics serveur\n${JSON.stringify(diagnostics, null, 2)}`);
	const truckRefreshContextItems = buildTruckRefreshDiagnosticContextItems(item, diagnostics);
	if (truckRefreshContextItems.length) {
		const truckLines = truckRefreshContextItems.flatMap((contextItem) => [
			`- ${contextItem.label || "Camion"}: ${contextItem.value || "n/d"}`,
			...normalizeInlineText(contextItem.detail).split("\n").filter(Boolean).map((line) => `  - ${line}`),
		]);
		sections.push(`## Modifications camion\n${truckLines.join("\n")}`);
	}
	if (isRun && Object.keys(diagnostics).length) {
		sections.push(`## Diagnostic compact\n${JSON.stringify(diagnostics, null, 2)}`);
	}
	if (requestMetrics.length) {
		sections.push(`## Chaîne serveur complète\n${JSON.stringify({
			capture_kind: diagnostics.capture_kind || null,
			operation_event_id: diagnostics.operation_event_id || item?.operationEventId || null,
			operation_action: diagnostics.operation_action || item?.operationAction || item?.action || null,
			operation_action_label: diagnostics.operation_action_label || item?.operationActionLabel || null,
			operation_status: diagnostics.operation_status || item?.status || null,
			started_at: diagnostics.started_at || null,
			completed_at: diagnostics.completed_at || null,
			operation_total_ms: diagnostics.operation_total_ms ?? item?.totalMs ?? null,
			server_work_ms: diagnostics.server_work_ms ?? null,
			request_count: diagnostics.request_count ?? requestMetrics.length,
			affected_order_count: diagnostics.affected_order_count ?? null,
			truck_refresh_details: Array.isArray(diagnostics.truck_refresh_details)
				? diagnostics.truck_refresh_details
				: null,
			request_metrics: requestMetrics,
		}, null, 2)}`);
	}
	if (!movementChanges.length) {
		return sections.join("\n\n");
	}
	const movementPayload = {
		movement_event_id: diagnostics.movement_event_id || null,
		moved_order_ids: Array.isArray(diagnostics.moved_order_ids) ? diagnostics.moved_order_ids : [],
		suspended_order_ids: Array.isArray(diagnostics.suspended_order_ids) ? diagnostics.suspended_order_ids : [],
		reactivated_order_ids: Array.isArray(diagnostics.reactivated_order_ids) ? diagnostics.reactivated_order_ids : [],
		released_order_ids: Array.isArray(diagnostics.released_order_ids) ? diagnostics.released_order_ids : [],
		movement_changes: movementChanges,
	};
	sections.push(`## Mouvements complets\n${JSON.stringify(movementPayload, null, 2)}`);
	return sections.join("\n\n");
}
